import { useState, useEffect, useRef } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";

export default function DropdownSelect({
    label,
    options = [],
    selected,
    onChange,
    multiple = false,
    error,
    disabled = false,
}) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const handleSelect = (value) => {
        if (disabled) return;
        if (multiple) {
            if (selected.includes(value)) {
                onChange(selected.filter((v) => v !== value));
            } else {
                onChange([...selected, value]);
            }
        } else {
            if (typeof value === "object") {
                onChange(value.value ?? value.id ?? value);
            } else {
                onChange(value);
            }
            setIsOpen(false);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <div className="flex-1 relative" ref={dropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
            <div
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={`w-full border rounded-lg px-3 py-2.5 flex justify-between items-center transition duration-150
                    ${disabled ? "bg-gray-100 border-gray-300 cursor-not-allowed" : "bg-white border-gray-300 cursor-pointer hover:border-blue-500"}`}
            >
                <span className={`${!selected || (Array.isArray(selected) && selected.length === 0) ? "text-gray-500" : "text-gray-800"}`}>
                    {multiple
                        ? selected.length > 0
                            ? `${selected.length} selected`
                            : `Select ${label}`
                        : (() => {
                            if (!selected) return `Select ${label}`;
                            if (typeof selected === "object") {
                                return selected.label || selected.name || selected.value || `Select ${label}`;
                            }
                            const match = options.find(opt => opt.value == selected);
                            return match?.label || `Select ${label}`;
                        })()
                    }
                </span>

                {isOpen && !disabled && <ChevronUpIcon className="w-5 h-5 text-gray-500" />}
                {!isOpen && <ChevronDownIcon className="w-5 h-5 text-gray-500" />}
            </div>

            {isOpen && !disabled && (
                <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-1 shadow-lg max-h-40 overflow-auto">
                    {options.map((item) => (
                        <li
                            key={item.value}
                            onClick={() => !multiple && handleSelect(item.value)}
                            className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-gray-800 flex items-center gap-2"
                        >
                            {multiple && (
                                <input
                                    type="checkbox"
                                    checked={selected.includes(item.value)}
                                    onChange={() => handleSelect(item.value)}
                                    className="text-blue-600 rounded"
                                />
                            )}
                            <span>{item.label}</span>
                        </li>
                    ))}
                </ul>
            )}
            {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        </div>
    );
}
